import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Consult, ConsultService } from "src/openapi"
import { Box, Modal, Typography } from "src/UILibrary"
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined"
import { useLoginContext } from "src/hooks/useLoginContext"
import InputTextField from "src/components/atom/InputTextField"
import { validateField } from "src/modules/validation"
import * as yup from "yup"
import { OutlinedButton } from "src/components/buttons/OutlinedButton"
import { CONSULT_STATUS, RESPONSE_CODE, SNACKBAR_TYPES } from "src/modules/const"
import { useRecoilState } from "recoil"
import { ShowToast } from "src/states/showToast"
import { ShowProgress } from "src/states/showProgress"

interface Props {
  item: Consult
  handleItem: any
  open: boolean
  handleOpen: any
}

export const ConsultResignModal = ({ item, handleItem, open, handleOpen }: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "mypage.inbox" })

  const { accessToken, checkLoginStatus } = useLoginContext()

  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const [reason, setReason] = useState<string>("")
  const [reasonValidationMessage, setReasonValidationMessage] = useState<string>("")

  const validate = async () => {
    try {
      await validateField(
        yup.object().shape({
          reason: yup.string().required(t("please_input_cancel_reason")),
        }),
        "reason",
        reason
      )
      setReasonValidationMessage("")
    } catch (err: any) {
      setReasonValidationMessage(err.message)
      return false
    }
    return true
  }

  const handleResign = async () => {
    if (!(await validate())) {
      return
    }

    setShowProgress(true)
    ConsultService.updateConsult(accessToken, item.id as number, {
      status: CONSULT_STATUS.RESIGNED,
      rejectReason: reason,
    })
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({
            show: true,
            text: t("success_resign_consult"),
            type: SNACKBAR_TYPES.SUCCESS,
          })
          handleOpen(false)
          handleItem(res.data)
        } else {
          setShowToast({ show: true, text: t("failed_resign_consult"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_resign_consult"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  return (
    <Modal open={open} onClose={() => handleOpen(false)}>
      <Box sx={{ p: 0 }}>
        <Box display="flex" height="50px" sx={{ borderBottom: "2px solid #D4D3D7" }}>
          <Typography.InputFieldTitle
            sx={{
              fontFamily: "Noto Sans JP !important",
              fontWeight: "700",
              ml: "10px",
              my: "auto",
            }}
          >
            {t("title_consult_resign")}
          </Typography.InputFieldTitle>
          <CloseOutlinedIcon
            sx={{
              my: "auto",
              ml: "auto",
              mr: "20px",
              width: "30px",
              height: "30px",
              ":hover": { opacity: 0.8, cursor: "pointer" },
            }}
            onClick={() => handleOpen(false)}
          />
        </Box>
        <Box display="flex" sx={{ mt: "20px" }}>
          <Box display="flex">
            <Typography.DetailText
              sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700" }}
            >
              {t("consult_member_name")}
            </Typography.DetailText>
            <Typography.DetailText
              sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", ml: "20px" }}
            >
              {`${item.member?.lastName} ${item.member?.firstName}`}
            </Typography.DetailText>
          </Box>
        </Box>
        <InputTextField
          name={t("resign_consult_step_1")}
          required
          multiline
          text={reason}
          handleText={setReason}
          sx={{ mt: "20px" }}
          inputStyle={{ width: "100%" }}
          labelStyle={{ fontWeight: "700", fontSize: "1rem" }}
          validationMessage={reasonValidationMessage}
        />
        <Typography.DetailText
          sx={{
            fontFamily: "Noto Sans JP !important",
            fontWeight: "700",
            color: "#FC3317",
            mt: "20px",
          }}
        >
          {t("resign_cannot_recover")}
        </Typography.DetailText>
        <OutlinedButton
          caption={t("resign_consult")}
          sx={{ mt: "20px", borderColor: "primary.main" }}
          fontColor="primary.main"
          onClick={() => handleResign()}
        />
      </Box>
    </Modal>
  )
}
